import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Link as LinkDeRota } from 'react-router-dom';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { formatarDataHora, formatarRelativo, parseInstanteOuNulo } from '../../../shared/dominio/datas';
import { caminhoDaRevisaoDaFonte } from '../../../shared/navegacao/mapaDeNavegacao';
import { EstadoDeCarregamento } from '../../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../../shared/ui/EstadoVazio';
import { opcoesDaFonte, opcoesDasPendencias } from '../api/contratos';
import { fraseDaSituacao, situacaoPedeAtencao } from '../api/situacaoDaFonte';
import type { SituacaoDaFonte } from '../api/situacaoDaFonte';
import { ROTULOS_DE_TIPO_DE_FONTE, type EstadoDaFonte, type FonteResumo } from '../api/tipos';
import { SeloDeEstadoDaFonte } from './SeloDeEstadoDaFonte';

/** `TP-050`: a revisão só faz sentido a partir de `regras_extraidas` — antes disso não há nada na fila. */
const ESTADOS_COM_REVISAO = new Set<EstadoDaFonte>(['regras_extraidas', 'em_revisao', 'ativa']);

/** Os códigos citados no texto que não viraram item, com a página: nada some em silêncio (BR-008 S3, F2). */
function PendenciasDaFonte({ fonteId }: { fonteId: string }): React.JSX.Element {
  const pendencias = useQuery(opcoesDasPendencias(fonteId));

  if (pendencias.isPending) return <>…</>;
  if (pendencias.isError) {
    return (
      <Typography variant="caption" color="error.main">
        Falhou ao carregar as pendências
      </Typography>
    );
  }

  return (
    <ul aria-label="Pendências da fonte" style={{ margin: 0, paddingLeft: '1.1em' }}>
      {pendencias.data.map((p) => (
        <li key={`${p.codigo}-${String(p.pagina)}`}>
          <Typography variant="caption">
            Código {p.codigo}, página {p.pagina}
            {p.resolvida ? ' (resolvida)' : ''}
          </Typography>
        </li>
      ))}
    </ul>
  );
}

/**
 * Por que a fonte está onde está (`TP-067`, `D12`). Sem a situação (API anterior ao refino), cai no "Parada
 * há X" de antes, que só sabia contar o tempo em `recebida`.
 */
function SituacaoDaLinha({
  fonte,
  situacao,
  criadaEm,
}: {
  fonte: FonteResumo;
  situacao: SituacaoDaFonte | null;
  criadaEm: Date | null;
}): React.JSX.Element | null {
  if (situacao === null) {
    return fonte.estado === 'recebida' && criadaEm !== null ? <>{`Parada ${formatarRelativo(criadaEm)}`}</> : null;
  }
  const frase = fraseDaSituacao(situacao, fonte.estado);
  if (frase === null) return null;
  return (
    <Typography variant="body2" color={situacaoPedeAtencao(situacao) ? 'warning.main' : 'text.secondary'}>
      {frase}
    </Typography>
  );
}

/**
 * Uma linha da lista: o resumo (`GET /contratos/{id}/fontes`) não traz
 * motivo, páginas nem contagens — só `GET /fontes/{id}` (T4) traz. Cada
 * linha busca o próprio detalhe; a lista costuma ter poucas fontes por
 * contrato (uma por tipo de documento), então N+1 aqui é barato.
 */
function LinhaDeFonte({ fonte }: { fonte: FonteResumo }): React.JSX.Element {
  const detalhe = useQuery(opcoesDaFonte(fonte.id));
  const criadaEm = parseInstanteOuNulo(fonte.criadaEm);

  return (
    <TableRow hover>
      <TableCell>{ROTULOS_DE_TIPO_DE_FONTE[fonte.tipo]}</TableCell>
      <TableCell>
        <SeloDeEstadoDaFonte estado={fonte.estado} />
      </TableCell>
      <TableCell sx={{ maxWidth: 360 }}>
        {detalhe.isError ? <Typography variant="caption" color="error.main">Falhou ao carregar o detalhe</Typography> : null}
        <SituacaoDaLinha fonte={fonte} situacao={detalhe.data?.situacao ?? null} criadaEm={criadaEm} />
        {detalhe.data?.motivoDaRecusa != null ? (
          <Typography variant="body2" color="text.secondary">
            Motivo da recusa: {detalhe.data.motivoDaRecusa}
          </Typography>
        ) : null}
      </TableCell>
      <TableCell>{detalhe.data?.paginas ?? '—'}</TableCell>
      <TableCell>{detalhe.data?.totalDeItens ?? '—'}</TableCell>
      <TableCell>
        {detalhe.data === undefined ? '—' : null}
        {detalhe.data?.totalDePendencias === 0 ? '0' : null}
        {detalhe.data !== undefined && detalhe.data.totalDePendencias > 0 ? <PendenciasDaFonte fonteId={fonte.id} /> : null}
      </TableCell>
      <TableCell>{criadaEm !== null ? formatarDataHora(criadaEm) : fonte.criadaEm}</TableCell>
      <TableCell>
        {ESTADOS_COM_REVISAO.has(fonte.estado) ? (
          <Button size="small" component={LinkDeRota} to={caminhoDaRevisaoDaFonte(fonte.id)}>
            {fonte.estado === 'ativa' ? 'Ver regras' : 'Revisar'}
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

export function ListaDeFontes({ consulta }: { consulta: UseQueryResult<FonteResumo[]> }): React.JSX.Element {
  return (
    <>
      {consulta.isPending ? <EstadoDeCarregamento linhas={3} /> : null}

      {consulta.isError ? (
        <EstadoDeErro
          erro={consulta.error}
          contexto="Não foi possível carregar as fontes deste contrato"
          aoReprocessar={() => {
            void consulta.refetch();
          }}
        />
      ) : null}

      {consulta.isSuccess && consulta.data.length === 0 ? (
        <EstadoVazio titulo="Nenhuma fonte enviada" descricao="Este contrato ainda não tem nenhum PDF enviado." />
      ) : null}

      {consulta.isSuccess && consulta.data.length > 0 ? (
        <TableContainer>
          <Table aria-label="Fontes do contrato" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell>Páginas</TableCell>
                <TableCell>Itens</TableCell>
                <TableCell>Códigos sem item</TableCell>
                <TableCell>Enviada em</TableCell>
                <TableCell>Revisão</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {consulta.data.map((fonte) => (
                <LinhaDeFonte key={fonte.id} fonte={fonte} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </>
  );
}
